import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { CheckCircle, Signature, Sparkle, Lock } from "@phosphor-icons/react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSignPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [note, setNote] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/sign/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setName(data.proposal?.recipient_name || "");
        setDesignation(data.proposal?.recipient_designation || "");
        if (data.already_signed) setDone(data.client_signature);
      })
      .catch((e) => setError(e?.response?.data?.detail || "This signing link is invalid or has been revoked."));
  }, [token]);

  const submit = async () => {
    if (!name || name.trim().length < 2) return toast.error("Please enter your full name");
    if (!sig) return toast.error("Please draw or upload your signature");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/sign/${token}`, {
        name: name.trim(), designation: designation.trim(), data_url: sig, note: note.trim(),
      });
      setDone({ name: name.trim(), designation: designation.trim(), data_url: sig, signed_at: data.signed_at, note: note.trim() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign");
    } finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="public-sign-error">
        <Lock size={32} className="mx-auto text-red-500" />
        <h1 className="font-display font-bold text-lg mt-3">Link unavailable</h1>
        <p className="text-sm text-slate-600 mt-2">{error}</p>
      </div>
    </div>
  );

  if (!info) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  const b = info.branding || {};
  const p = info.proposal || {};

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-sign-page">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {b.logo_data_uri
            ? <img src={b.logo_data_uri} alt="Logo" className="h-9 object-contain"/>
            : <img src="/tn-icon.png" alt="thinkneural.ai" className="h-9 w-9"/>}
          <div>
            <div className="text-[10px] tracking-widest uppercase font-bold" style={{ color: b.accent_color }}>{b.company_name}</div>
            <div className="font-display font-bold text-slate-900 text-sm">{b.cover_title}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8">
          <div className="label-micro">Electronic signature</div>
          <h1 className="font-display font-black text-2xl sm:text-3xl mt-1" style={{ color: b.primary_color }}>Sign your proposal</h1>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-6 text-sm">
            <div><span className="font-semibold">Client:</span> {p.client_legal_name}</div>
            <div><span className="font-semibold">Type:</span> <span className="capitalize">{p.proposal_type}</span></div>
            <div><span className="font-semibold">Issue date:</span> {p.issue_date}</div>
            <div><span className="font-semibold">Valid for:</span> {p.validity_days} days</div>
            <div><span className="font-semibold">Prepared by:</span> {p.prepared_by_name}</div>
            <div><span className="font-semibold">Status:</span> <span className="capitalize">{p.status?.replace(/_/g,' ')}</span></div>
          </div>

          {/* Full proposal preview */}
          {(p.sections || []).length > 0 && (
            <div className="mt-6 border-t border-slate-200 pt-6 space-y-5" data-testid="public-sign-proposal-body">
              <div className="label-micro">Proposal contents</div>
              {(p.sections || []).map((s, i) => (
                <section key={i}>
                  <h2 className="font-display font-bold text-base sm:text-lg" style={{ color: b.primary_color }}>{s.heading}</h2>
                  {s.body && <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed mt-1">{s.body}</div>}
                  {s.pricing && s.pricing.length > 0 && (
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs sm:text-sm border-collapse min-w-[520px]">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold">Item</th>
                            <th className="text-left px-2 py-1.5 font-semibold">Mode</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Qty</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Unit</th>
                            <th className="text-right px-2 py-1.5 font-semibold">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.pricing.map((l, j) => (
                            <tr key={j} className="border-b border-slate-100">
                              <td className="px-2 py-1.5">{l.label} <span className="text-[10px] text-slate-500">({l.charge_type})</span></td>
                              <td className="px-2 py-1.5 text-xs">{l.mode}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{l.quantity}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">₹{(l.unit_price/100).toLocaleString('en-IN')}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums font-semibold">₹{(l.total_amount/100).toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {p.totals && (
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          <div><div className="label-micro">One-time</div><div className="font-bold tabular-nums">₹{(p.totals.one_time_paise/100).toLocaleString('en-IN')}</div></div>
                          <div><div className="label-micro">Recurring/mo</div><div className="font-bold tabular-nums">₹{(p.totals.recurring_monthly_paise/100).toLocaleString('en-IN')}</div></div>
                          <div><div className="label-micro">Year-1 total</div><div className="font-bold tabular-nums" style={{ color: b.accent_color }}>₹{(p.totals.year_one_total_paise/100).toLocaleString('en-IN')}</div></div>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              ))}
            </div>
          )}

          {done ? (
            <div className="mt-8 border border-emerald-300 bg-emerald-50 rounded-sm p-5" data-testid="public-sign-done">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-emerald-600"/>
                <div className="font-display font-bold text-emerald-800">Signed successfully</div>
              </div>
              <p className="text-sm text-emerald-800 mt-2">
                Thank you, <b>{done.name}</b>{done.designation ? ` (${done.designation})` : ""} — your signature was recorded on {new Date(done.signed_at).toLocaleString()}.
              </p>
              <div className="mt-4 bg-white border border-slate-200 p-3 max-w-xs">
                <img src={done.data_url} alt="Your signature" className="max-h-16 object-contain"/>
                <div className="text-xs text-slate-500 mt-1">{done.name}{done.designation ? ` · ${done.designation}` : ""}</div>
              </div>
              {done.note && <p className="text-xs text-emerald-900 mt-3 italic">Your note: "{done.note}"</p>}
              <p className="text-[11px] text-slate-500 mt-4">You may close this window. A copy of the signed proposal has been shared with the sender.</p>
            </div>
          ) : (
            <div className="mt-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block"><span className="label-micro">Full name *</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-sign-name"
                         className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/></label>
                <label className="block"><span className="label-micro">Designation</span>
                  <input value={designation} onChange={(e) => setDesignation(e.target.value)} data-testid="public-sign-designation"
                         className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/></label>
              </div>
              <div>
                <div className="label-micro mb-1">Your signature *</div>
                <SignaturePad value={sig} onChange={setSig} testid="public-sig"/>
              </div>
              <label className="block"><span className="label-micro">Optional note</span>
                <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="public-sign-note"
                          placeholder="e.g., Accepted — please proceed with kickoff."
                          className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/></label>
              <div className="text-[11px] text-slate-500 border-l-2 border-blue-500 bg-blue-50 pl-3 py-2">
                By signing below, you confirm that the person named above has authority to accept this proposal on behalf of <b>{p.client_legal_name}</b>. A timestamped copy will be shared with the sender.
              </div>
              <button onClick={submit} disabled={busy} data-testid="public-sign-submit"
                      className="w-full sm:w-auto text-sm rounded-sm px-6 py-2.5 text-white bg-slate-900 hover:bg-blue-700 inline-flex items-center gap-2 transition-colors disabled:opacity-60"
                      style={{ backgroundColor: busy ? undefined : b.primary_color }}>
                <Signature size={14}/> {busy ? "Signing…" : "Sign & accept proposal"}
              </button>
            </div>
          )}
        </div>
        {b.footer_text && <div className="text-[10px] italic text-slate-500 mt-4 text-center">{b.footer_text}</div>}
      </main>
    </div>
  );
}
